import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { supabase } from '../lib/supabaseClient.js'
import { fetchApi } from '../db/api.js'
import { LEGAL_VERSION } from '../lib/legalVersion.js'

const AuthContext = createContext(null)

// O cadastro só é enviado com o aceite marcado; o banco carimba a data (handle_new_user).
const TERMS_CONSENT = { terms_accepted: true, terms_version: LEGAL_VERSION }

function hasRecoveryParams() {
  if (typeof window === 'undefined') {
    return false
  }

  const search = new URLSearchParams(window.location.search)
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''))

  return (
    search.get('type') === 'recovery' ||
    hash.get('type') === 'recovery' ||
    search.has('code') ||
    search.has('token_hash') ||
    hash.has('access_token')
  )
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null)
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [authEvent, setAuthEvent] = useState(null)
  const [recoveryIntent, setRecoveryIntent] = useState(hasRecoveryParams())

  useEffect(() => {
    let active = true

    // Confiamos na sessao que o supabase-js mantem em localStorage e nos
    // eventos de onAuthStateChange. NAO chamamos getUser() de novo: o
    // session.user ja vem com a sessao validada pelo Supabase no signIn.
    // Revalidar via getUser introduz uma janela onde qualquer falha de
    // rede/cors/rate-limit derrubava a sessao e o usuario "saia logo apos
    // entrar".
    const { data: sub } = supabase.auth.onAuthStateChange((nextEvent, nextSession) => {
      if (!active) return

      console.debug('[auth] event:', nextEvent, 'session?', !!nextSession)
      setAuthEvent(nextEvent)

      if (nextEvent === 'PASSWORD_RECOVERY') {
        setRecoveryIntent(true)
      }
      if (nextEvent === 'SIGNED_OUT') {
        setRecoveryIntent(false)
      }

      setSession(nextSession ?? null)
      setUser(nextSession?.user ?? null)
      // Loading so eh true durante o boot inicial. Eventos posteriores
      // (TOKEN_REFRESHED, USER_UPDATED) nao devem repintar a UI inteira.
      setLoading(false)
    })

    // Boot: pega sessao ja persistida (sem network call extra).
    supabase.auth.getSession().then(({ data, error }) => {
      if (!active) return
      if (error) {
        console.error('[auth] getSession falhou:', error)
      }
      // Se onAuthStateChange ja disparou INITIAL_SESSION antes desse then,
      // o estado ja esta correto. Aqui so garantimos que loading sai do ar
      // mesmo se nao houver sessao alguma.
      setSession((current) => current ?? data.session ?? null)
      setUser((current) => current ?? data.session?.user ?? null)
      setLoading(false)
    })

    return () => {
      active = false
      sub.subscription.unsubscribe()
    }
  }, [])

  const signIn = (email, password) => supabase.auth.signInWithPassword({ email, password })

  const signUp = (email, password, fullName, inviteCode, phone) =>
    supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
          invite_code: inviteCode.trim().toUpperCase(),
          phone: phone?.trim() || null,
          ...TERMS_CONSENT,
        },
      },
    })

  // Fluxo 2 (entrar em organização): cadastro SEM access_invite. Carrega o
  // código do convite de organização em `org_invite_code` no metadata, para que
  // o join possa rodar após a confirmação/login. NÃO cria organização — quem
  // materializa o acesso é POST /api/me/join-organization. signUp (Fluxo 1)
  // permanece intocado.
  const signUpForJoin = (email, password, fullName, phone, orgInviteCode) =>
    supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
          phone: phone?.trim() || null,
          org_invite_code: orgInviteCode?.trim().toUpperCase() || null,
          ...TERMS_CONSENT,
        },
      },
    })

  const resendSignupConfirmation = (email) =>
    supabase.auth.resend({
      type: 'signup',
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/login`,
      },
    })

  const resetPassword = (email) =>
    supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/redefinir-senha`,
    })

  const updatePassword = (password) =>
    supabase.auth.updateUser({
      password,
    })

  // Atualiza user_metadata (ex.: { full_name }). Dispara USER_UPDATED, que
  // o AuthProvider escuta e propaga para `user` — UI reflete sem reload.
  const updateUserMetadata = (data) =>
    supabase.auth.updateUser({ data })

  const exchangeRecoveryCode = (code) => supabase.auth.exchangeCodeForSession(code)

  // Issue #253: organizações, convite de acesso e onboarding passam pelo backend
  // (antes: PostgREST/RPC direto). Mantém o formato { data, error } que as telas
  // já consumiam, então só o transporte mudou.
  const asResult = (promise) =>
    promise.then((data) => ({ data, error: null })).catch((error) => ({ data: null, error }))

  const validateInvite = (code) => {
    const normalizedCode = code.trim().toUpperCase()
    return asResult(fetchApi(`/public/access-invites/${encodeURIComponent(normalizedCode)}`))
  }

  // Fase 5 (B7): memoizado para evitar refetch redundante no AppContext.
  // Sem useCallback, cada render do AuthProvider criaria uma nova função e
  // o useEffect do AppContext (que tem `listOrganizations` nas deps) dispararia
  // 4 requests redundantes (/me/access, /clients, listOrganizations, /accounts).
  const listOrganizations = useCallback(
    () => fetchApi('/me/organizations').then((data) => ({ data, error: null })).catch((error) => ({ data: null, error })),
    []
  )

  const createInitialOrganization = (name, type) =>
    asResult(fetchApi('/me/organization', { method: 'POST', body: JSON.stringify({ name: name.trim(), type }) }))

  const signOut = () => supabase.auth.signOut()

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        loading,
        isAuthenticated: !!user,
        isRecoverySession:
          authEvent === 'PASSWORD_RECOVERY' || (recoveryIntent && !!session),
        signIn,
        signUp,
        signUpForJoin,
        resendSignupConfirmation,
        resetPassword,
        updatePassword,
        updateUserMetadata,
        exchangeRecoveryCode,
        validateInvite,
        listOrganizations,
        createInitialOrganization,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth precisa estar dentro de AuthProvider')
  }
  return ctx
}
